
import { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useTransform,
  useScroll,
} from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import logo from "../../Assets/Logos/W-logo.png";
import Blogo from "../../Assets/Logos/B-logo.png";
import { navLinks } from "./navbarData";
import { Check, Menu } from "lucide-react";
import DemoBookingAssistant from "../User/Chatbot/Demobookingassistant";

function NavDropdown({ dropdown, scrolled, onNavigate, onDemoClick }) {
  const itemHover = "group-hover:text-orange-500";

  const widthMap = {
    cards: "w-[800px]",
    "icon-list": "w-[320px]",
    simple: "w-[240px]",
  };

  const widthClass = dropdown.width ?? widthMap[dropdown.type] ?? "w-[320px]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={`absolute top-full ${dropdown.left} -translate-x-1/2 mt-3 ${widthClass} bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden`}
    >
      <div className="p-4">
        {/* ── CARDS layout (Services) ── */}
        {dropdown.type === "cards" && (
          <>
            <div className="flex gap-8 relative ">
              <div className="">
                <p className="text-xs font-bold tracking-widest uppercase mb-3 px-1 text-gray-400">
                  What We Offer
                </p>
                <div className="grid grid-cols-1 gap-1">
                  {dropdown.items.map((item, i) => (
                    <motion.button
                      key={item.path}
                      onClick={() => onNavigate(item.path)}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04, duration: 0.2 }}
                      whileHover={{ x: 4 }}
                      className="flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-150 group text-left w-full"
                    >
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 transition-all bg-orange-50 group-hover:bg-orange-100">
                        {item.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-bold text-sm text-gray-900 transition-colors ${itemHover}`}
                        >
                          {item.label}
                        </p>
                        <p className="mt-0.5 truncate font-semibold text-sm text-gray-500">
                          {item.desc}
                        </p>
                      </div>
                    </motion.button>
                  ))}
                </div>
                {/* {dropdown.footer && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between px-1">
            <span className="text-xs text-gray-400">{dropdown.footer.text}</span>
            <button
              onClick={() => onNavigate(dropdown.footer.path)}
              className="text-xs font-bold text-orange-500 hover:text-orange-700 transition-colors"
            >
              {dropdown.footer.cta}
            </button>
          </div>
        )} */}
              </div>

              {/* Right panel — added bg-gradient-to-br */}
              <div className="relative  p-6 flex-1 flex flex-col justify-between h-auto text-white rounded-xl bg-gradient-to-br from-orange-500 via-orange-400 to-yellow-400">
               
                <div>
                  <h1 className="text-lg font-semibold mb-3 tracking-tight">
                    We are here to help
                  </h1>
                  <p className="text-sm text-white/90 leading-relaxed mb-4">
                    No matter the stage of your idea or project, let's plan your
                    next move with clarity.
                  </p>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3">
                      <span className="bg-white/20 backdrop-blur-md p-1.5 rounded-full">
                        <Check size={14} />
                      </span>
                      <p className="text-sm text-white/90">
                        Expert guidance tailored to your needs
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-white/20 backdrop-blur-md p-1.5 rounded-full">
                        <Check size={14} />
                      </span>
                      <p className="text-sm text-white/90">
                        Fast and efficient planning sessions
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onDemoClick}
                  className="mt-6 w-full cursor-pointer bg-white text-orange-600 text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-orange-50 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
                >
                  Schedule a call →
                </button>
              </div>
            </div>
          </>
        )}
        {dropdown.type === "icon-list" &&
          (() => {
            const isGrouped = dropdown?.items?.[0]?.options !== undefined;

            // ── Flat (Industries) ──
            if (!isGrouped) {
              return (
                <div
                  className={`grid gap-0.5 ${dropdown.columns === 2 ? "grid-cols-2" : "grid-cols-1"}`}
                >
                  {dropdown.items?.map((item, i) => (
                    <motion.button
                      key={i}
                      whileHover={{
                        x: 3,
                        backgroundColor: scrolled
                          ? "#fff7ed"
                          : "rgba(255,255,255,0.08)",
                      }}
                      onClick={() => onNavigate(item.path)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-left w-full group"
                    >
                      <span
                        className={`text-xl w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all bg-orange-50 group-hover:bg-orange-100`}
                      >
                        {item.icon}
                      </span>
                      <p
                        className={`font-bold text-sm text-gray-900 hover:text-orange-500 transition-colors`}
                      >
                        {item.label}
                      </p>
                    </motion.button>
                  ))}
                </div>
              );
            }

            const cols = dropdown.columns ?? 2;

            return (
              <div className="flex justify-between gap-6 w-full">
                {/* LEFT SIDE - SCROLLABLE LIST */}
                <div className="max-h-[400px] grid grid-cols-3">
                  {dropdown.items?.map((section, si) => (
                    <div key={si} className="mb-4">
                      <p
                        className={` font-bold text-sm text-gray-900 mb-2 px-1 border-l-2 border-orange-400 pl-2 transition-colors`}
                      >
                        {section.heading}
                      </p>

                      <div className="flex flex-col gap-0.5">
                        {section.options?.map((item, i) => (
                          <motion.button
                            key={i}
                            whileHover={{
                              x: 3,
                              backgroundColor: scrolled
                                ? "#fff7ed"
                                : "rgba(255,255,255,0.08)",
                            }}
                            onClick={() => onNavigate(item.path)}
                            className="flex items-center gap-2.5 px-2 py-2 rounded-lg cursor-pointer transition-all text-left w-full group"
                          >
                            <span
                              className={`text-base w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-all $bg-orange-50 group-hover:bg-orange-100 `}
                            >
                              {item.icon}
                            </span>

                            <p
                              className={`font-semibold text-sm text-gray-500 group-hover:text-orange-500 transition-colors leading-tight`}
                            >
                              {item.label}
                            </p>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* RIGHT SIDE - INFO PANEL */}
                <div className="w-[280px] shrink-0 relative rounded-2xl overflow-hidden shadow-xl">
                  {/* Background Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-400 to-yellow-400" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.25),transparent_60%)]" />

                  {/* Content */}
                  <div className="relative p-6 flex flex-col justify-between h-full text-white">
                    {/* TOP CONTENT */}
                    <div>
                      <h1 className="text-lg font-semibold mb-3 tracking-tight">
                        We are here to help
                      </h1>

                      <p className="text-sm text-white/90 leading-relaxed mb-4">
                        No matter the stage of your idea or project, let’s plan
                        your next move with clarity.
                      </p>

                      {/* BULLET POINTS */}
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                          <span className="bg-white/20 backdrop-blur-md p-1.5 rounded-full">
                            <Check size={14} />
                          </span>
                          <p className="text-sm text-white/90">
                            Expert guidance tailored to your needs
                          </p>
                        </div>

                        <div className="flex items-start gap-3">
                          <span className="bg-white/20 backdrop-blur-md p-1.5 rounded-full">
                            <Check size={14} />
                          </span>
                          <p className="text-sm text-white/90">
                            Fast and efficient planning sessions
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <button
                      onClick={onDemoClick}
                      className="mt-6 w-full bg-white text-orange-600 text-sm font-semibold px-4 py-2.5 rounded-lg 
                  hover:bg-orange-50 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
                    >
                      Schedule a call →
                    </button>
                    {/* <ScheduleCallButton /> */}
                  </div>
                </div>
              </div>
            );
          })()}

        {/* ── SIMPLE layout (Company) ── */}
        {dropdown.type === "simple" && (
          <div className="">
            {dropdown.items.map((item, i) => (
              <motion.button
                key={i}
                whileHover={{ x: 4 }}
                onClick={() => onNavigate(item.path)}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04, duration: 0.2 }}
                className={`w-full text-left px-4 py-2.5 rounded-xl hover:bg-orange-50 transition-all`}
              >
                <p
                  className={`font-bold text-[15px] text-gray-900  hover:text-orange-500 transition-colors`}
                >
                  {item.label}
                </p>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Hamburger Icon ──────────────────────────────────────────────────────────
function HamburgerIcon({ open, scrolled }) {
  const color = scrolled ? "bg-black" : "bg-white";

  return (
    <div className="w-6 h-5 flex flex-col justify-between">
      <motion.span
        animate={open ? { rotate: 45, y: 9 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`block h-0.5 w-full rounded origin-center ${color}`}
      />

      <motion.span
        animate={open ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={`block h-0.5 w-full rounded ${color}`}
      />

      <motion.span
        animate={open ? { rotate: -45, y: -9 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`block h-0.5 w-full rounded origin-center ${color}`}
      />
    </div>
  );
}

// ─── Mobile Nav ───────────────────────────────────────────────────────────────
function MobileNav({ isOpen, navLinks, onNavigate, onDemoClick }) {
  const [openItems, setOpenItems] = useState({});

  const toggle = (label) =>
    setOpenItems((prev) => ({ ...prev, [label]: !prev[label] }));

  // Detect if an icon-list dropdown uses grouped data { heading, options[] }
  const isGroupedIconList = (dropdown) =>
    dropdown?.type === "icon-list" &&
    dropdown.items?.[0]?.options !== undefined;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="mobile-menu"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.28, ease: "easeInOut" }}
          className="lg:hidden overflow-hidden bg-white border-t border-gray-100 shadow-lg"
        >
          <div className="max-h-[calc(100vh-70px)] overflow-y-auto">
            <ul className="flex flex-col px-4 py-3 gap-0.5">
              {navLinks.map((link) => {
                const hasDropdown = !!link.dropdown;
                const isOpen = openItems[link.label];

                const grouped = isGroupedIconList(link.dropdown);

                return (
                  <motion.li
                    key={link.label}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <button
                      onClick={() =>
                        hasDropdown ? toggle(link.label) : onNavigate(link.path)
                      }
                      className="w-full flex items-center justify-between py-3 px-3 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                    >
                      <span>{link.label}</span>
                      {hasDropdown && (
                        <motion.span
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.22 }}
                        >
                          <IoIosArrowDown size={15} />
                        </motion.span>
                      )}
                    </button>

                    <AnimatePresence>
                      {isOpen && hasDropdown && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden ml-3 border-l-2 border-orange-200 pl-3 mb-2"
                        >
                          {/* Grouped icon-list: { heading, options[] } — Product Suite */}
                          {grouped
                            ? link.dropdown.items.map((section, si) => (
                                <div key={si} className="mt-2">
                                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest mb-1 px-2">
                                    {section.heading}
                                  </p>
                                  {section.options.map((item, ii) => (
                                    <button
                                      key={ii}
                                      onClick={() => {
                                        onNavigate(item.path);
                                        toggle(link.label);
                                      }}
                                      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-orange-50 group transition-all"
                                    >
                                      <span className="text-base w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center shrink-0">
                                        {item.icon}
                                      </span>
                                      <p className="text-xs font-medium text-gray-700 group-hover:text-orange-600">
                                        {item.label}
                                      </p>
                                    </button>
                                  ))}
                                </div>
                              ))
                            : /* Flat items: cards / icon-list / simple */
                              link.dropdown.items.map((item, i) => (
                                <button
                                  key={i}
                                  onClick={() => {
                                    onNavigate(item.path);
                                    toggle(link.label);
                                  }}
                                  className="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl text-left hover:bg-orange-50 group transition-all"
                                >
                                  {item.icon && (
                                    <span className="text-lg w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                                      {item.icon}
                                    </span>
                                  )}
                                  <div>
                                    <p className="font-semibold text-sm text-gray-800 group-hover:text-orange-600">
                                      {item.label}
                                    </p>
                                    {item.desc && (
                                      <p className="text-gray-400 text-xs mt-0.5">
                                        {item.desc}
                                      </p>
                                    )}
                                  </div>
                                </button>
                              ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.li>
                );
              })}
            </ul>

            {/* Mobile CTA Buttons */}
            <div className="flex flex-col gap-3 px-4 py-4 border-t border-gray-100">
              <button
                onClick={() => onNavigate("/contact")}
                className="w-full py-3 rounded-full border-2 border-orange-500 text-orange-600 font-semibold text-sm hover:bg-orange-50 transition-all"
              >
                Contact Us
              </button>
              <button
                onClick={onDemoClick}
                className="w-full py-3 rounded-full bg-gray-800 text-white font-semibold text-sm hover:bg-orange-500 transition-all"
              >
                Get a Demo
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Main Navbar ─────────────────────────────────────────────────────────────
export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false); // NEW: track if animation has played

  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const hoverTimer = useRef(null);
  const scrollTimer = useRef(null); // NEW: for debouncing

  // Active label detection — handles flat items and grouped { heading, options[] }
  const activeLabel = (() => {
    for (const link of navLinks) {
      if (link.path === location.pathname) return link.label;
      if (link.dropdown) {
        const isGrouped = link.dropdown.items?.[0]?.options !== undefined;
        const allItems = isGrouped
          ? link.dropdown.items.flatMap((s) => s.options)
          : link.dropdown.items || [];
        if (allItems.some((item) => item.path === location.pathname))
          return link.label;
      }
    }
    return "";
  })();

  const handleNavigate = (path) => {
    setMobileOpen(false);
    setHoveredItem(null);
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleMouseEnter = (label) => {
    clearTimeout(hoverTimer.current);
    setHoveredItem(label);
  };

  const handleMouseLeave = () => {
    hoverTimer.current = setTimeout(() => setHoveredItem(null), 180);
  };

  useEffect(() => {
    const onOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target))
        setHoveredItem(null);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setHoveredItem(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // NEW: Trigger animation when user starts scrolling
      if (window.scrollY > 10 && !hasAnimated) {
        setHasAnimated(true);
      }
    };

    // NEW: Check if user already scrolled before component mounted
    if (window.scrollY > 10 && !hasAnimated) {
      setHasAnimated(true);
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasAnimated]);

  const hoverColor = "hover:text-orange-500";
  const textColor = scrolled ? "text-gray-800" : "text-gray-800";

  const onDemoClick = () => {
    setModalOpen(true);
  };

  // Animation variants for the underline (same as Volto Consulting)
  const underlineVariants = {
    rest: { width: "0%" },
    hover: {
      width: "100%",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  return (
    <>
      <motion.header
        animate={{
          y: scrolled || window.scrollY > 10 ? 0 : 0,
          transition: {
            type: "spring",
            stiffness: 400,
            damping: 35,
          },
        }}
        className={`w-full sticky top-0 z-50 backdrop-blur-sm transition-all duration-300  ${
          scrolled ? "bg-white/80  shadow-md" : "bg-transparent"
        }`}
      >
        <nav
          ref={navRef}
          className="max-w-7xl mx-auto px-4 sm:px-6 h-[70px] sm:h-[90px] flex items-center justify-between transition-[height,padding] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
        >
          {/* Logo - Remove initial animation or make it scroll-triggered */}
          <motion.div
            initial={{ opacity: 1, x: 0 }} // CHANGED: start visible
            animate={{ opacity: 1, x: 0 }}
            className="cursor-pointer shrink-0"
            onClick={() => handleNavigate("/")}
          >
            <img
              src={scrolled ? Blogo : Blogo}
              alt="Logo"
              className="w-auto object-cover h-14 sm:h-20"
            />
          </motion.div>

          {/* Desktop Nav Links - Remove initial animation */}
          <ul className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, i) => {
              const isActive = activeLabel === link.label;
              const hasDropdown = !!link.dropdown;
              const isHovered = hoveredItem === link.label;

              return (
                <motion.li
                  key={link.label}
                  className="relative"
                  initial={{ opacity: 1, y: 0 }} // CHANGED: start visible
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.3 }}
                  onMouseEnter={() =>
                    hasDropdown && handleMouseEnter(link.label)
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    onClick={() => !hasDropdown && handleNavigate(link.path)}
                    className={`relative px-3 py-2 text-base flex items-center gap-1.5 font-semibold rounded-lg transition-all duration-200 ${
                      isActive ? "text-[#feb501]" : `${textColor} ${hoverColor}`
                    }`}
                  >
                    {link.label}
                    {hasDropdown && (
                      <motion.span
                        animate={{ rotate: isHovered ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <IoIosArrowDown size={14} />
                      </motion.span>
                    )}

                    {/* Active page underline */}
                    {isActive && !hasDropdown && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[#feb501] rounded"
                        transition={{
                          type: "spring",
                          stiffness: 380,
                          damping: 32,
                        }}
                      />
                    )}

                    {/* Hover underline animation */}
                    {!hasDropdown && !isActive && (
                      <motion.span
                        className="absolute left-3 -bottom-0.5 h-0.5 bg-[#feb501] rounded"
                        variants={underlineVariants}
                        initial="rest"
                        whileHover="hover"
                        animate="rest"
                      />
                    )}
                  </button>

                  {/* One unified dropdown renderer */}
                  <AnimatePresence>
                    {isHovered && hasDropdown && (
                      <NavDropdown
                        dropdown={link.dropdown}
                        scrolled={scrolled}
                        onDemoClick={onDemoClick}
                        onNavigate={handleNavigate}
                      />
                    )}
                  </AnimatePresence>
                </motion.li>
              );
            })}
          </ul>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={() => handleNavigate("/contact")}
              className={`font-semibold text-base transition-colors ${textColor} ${hoverColor}`}
            >
              Contact Us
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className={`px-5 py-2.5 rounded-full border-2 text-base font-semibold bg-blue-600 transition-all duration-200 border-blue-900 text-white hover:bg-[#fdb602]`}
            >
              Get a Demo
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            className={`lg:hidden p-2 ${scrolled ? "text-black" : "text-white"}`}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <Menu size={34} />
          </button>
        </nav>

        {/* Mobile Menu */}
        <MobileNav
          isOpen={mobileOpen}
          navLinks={navLinks}
          onNavigate={handleNavigate}
          onDemoClick={() => {
            setMobileOpen(false);
            setModalOpen(true);
          }}
        />
      </motion.header>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end sm:pr-6 sm:pb-6 bg-black/40 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
        >
          <DemoBookingAssistant
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            autoStart={true}
          />
        </div>
      )}
    </>
  );
}
